import React, { FC } from 'react';
import Box from '@mui/material/Box';
import { Form, Formik, FormikErrors, FormikValues } from 'formik';
import { useAppSelector } from '../../../redux/hooks';
import useAuth from '../../../providers/Auth/Auth.provider';
import DateUtils from '../../../utils/dateUtils';
import useNotifications from '../../../providers/Notifications/Notifications.provider';

interface TripWrapperFormProps {
  children: React.ReactNode;
  onSubmit: () => void;
  formRef: React.RefObject<HTMLFormElement>;
}

const TripWrapperForm: FC<TripWrapperFormProps> = ({ children, onSubmit, formRef }) => {
  const { getTenantSetting } = useAuth();
  const { addNotification } = useNotifications();
  const { provider_trip_stops } = useAppSelector((state) => state.trip);
  const enableAppointmentWindow = getTenantSetting('enable-appointment-window', false);

  const getErrorInfo = (values: FormikValues): [FormikErrors<FormikValues>, string | undefined] => {
    const errorDateMessage = 'Date validation failed: please ensure date is correct.';
    const errorTimeMessage = 'Time validation failed: please ensure time is correct.';
    const errors: any = {};

    let errorInputName: string | undefined = undefined;

    // stop dates check
    if (provider_trip_stops && provider_trip_stops.length > 0) {
      for (let i = 0; i < provider_trip_stops.length; i++) {
        const stop = provider_trip_stops[i];
        const nextStop = provider_trip_stops[i + 1];
        if (!stop.provider_date) {
          errors[`stop-date-${stop.order}`] = `Missing required field: Stop ${stop.order} date`;
          errorInputName = `stop-date-${stop.order}`;
          break;
        }

        if (enableAppointmentWindow) {
          // All validation for appointment window enabled

          // Validate if stop has provider_date and next stop has provider_date
          if (nextStop && nextStop.provider_date && stop.provider_date && nextStop.provider_date) {
            const currentStopDate = DateUtils.getDateInLocale(new Date(stop.provider_date));
            const nextStopDate = DateUtils.getDateInLocale(new Date(nextStop.provider_date));

            // Throw error if next stop date is less than current stop date
            if (nextStopDate < currentStopDate) {
              errors[`stop-date-${nextStop.order}`] = errorDateMessage;
              errorInputName = `stop-date-${nextStop.order}`;

              break;
            }
          }

          // Throw error if time to is less than time from
          if (stop.provider_time_from && stop.provider_time_to) {
            if (stop.provider_time_to.setSeconds(0, 0) < stop.provider_time_from.setSeconds(0, 0)) {
              errors[`stop-time-to-${stop.order}`] = errorTimeMessage;
              errorInputName = `stop-time-to-${stop.order}`;

              break;
            }
          }

          // Throw error if time to is provided and time from is not
          if (!stop.provider_time_from && stop.provider_time_to) {
            errors[`stop-time-from-${stop.order}`] = errorTimeMessage;
            errorInputName = `stop-time-from-${stop.order}`;

            break;
          }

          // If stop has prvider_date and provider_time_from,
          // check if there exists any previous stop with same date and time from in order to test if
          // time from of stop is greater than time from of previous stop
          // Throw error if any previous stop has same date and time from greater than current stop time from
          if (stop.provider_date && stop.provider_time_from) {
            // check if there exists any previous stop with same date and time from in order to test if it is a duplicate
            const previousStops = provider_trip_stops.slice(0, i);
            const isThereAnyPreviousStopWithSameDateAndTimeFromGrater = previousStops.some((s) => {
              // Temporarily return false to fix: https://app.clickup.com/t/10558201/TW-5340
              return false;
            });

            const isThereAnyPreviousStopWithSameDateAndTimeToGrater = previousStops.some((s) => {
              // Temporarily return false to fix: https://app.clickup.com/t/10558201/TW-5340
              return false;
            });

            if (
              isThereAnyPreviousStopWithSameDateAndTimeFromGrater ||
              isThereAnyPreviousStopWithSameDateAndTimeToGrater
            ) {
              errors[`stop-time-from-${stop.order}`] = errorTimeMessage;
              errorInputName = `stop-time-from-${stop.order}`;

              break;
            }
          }
        } else {
          // All validation for appointment window disabled

          // Validate if stop has provider_date and next stop has provider_date
          if (nextStop && nextStop.provider_date && stop.provider_date && nextStop.provider_date) {
            const currentStopDate = DateUtils.getDateInLocale(new Date(stop.provider_date));
            const nextStopDate = DateUtils.getDateInLocale(new Date(nextStop.provider_date));

            // Throw error if next stop date is less than current stop date
            if (nextStopDate < currentStopDate) {
              errors[`stop-date-${nextStop.order}`] = errorDateMessage;
              errorInputName = `stop-date-${nextStop.order}`;

              break;
            }
          }

          // If stop has prvider_date and provider_time_from,
          // check if there exists any previous stop with same date and time from in order to test if
          // time from of stop is greater than time from of previous stop
          // Throw error if any previous stop has same date and time from greater than current stop time from
          if (stop.provider_date && stop.provider_time_from) {
            // check if there exists any previous stop with same date and time from in order to test if it is a duplicate
            const previousStops = provider_trip_stops.slice(0, i);
            const isThereAnyPreviousStopWithSameDateAndTimeFromGrater = previousStops.some((s) => {
              // Temporarily return false to fix: https://app.clickup.com/t/10558201/TW-5340
              return false;
            });

            if (isThereAnyPreviousStopWithSameDateAndTimeFromGrater) {
              errors[`stop-time-from-${stop.order}`] = errorTimeMessage;
              errorInputName = `stop-time-from-${stop.order}`;

              break;
            }
          }
        }
      }
      if (errorInputName) return [errors, errorInputName];
    }

    // stop times check
    if (
      provider_trip_stops &&
      provider_trip_stops.length > 0 &&
      provider_trip_stops.some(
        (s) =>
          // Validate if s.provider_time_from and s.provider_time_to have correct HH:MM format.
          (s.provider_time_from &&
            s.provider_time_from !== null &&
            !DateUtils.validateTimeFromDate(s.provider_time_from)) ||
          (s.provider_time_to &&
            s.provider_time_to !== null &&
            !DateUtils.validateTimeFromDate(s.provider_time_to))
      )
    ) {
      for (let i = 0; i < provider_trip_stops.length; i++) {
        const stop = provider_trip_stops[i];
        if (!!stop.provider_time_from && !DateUtils.validateTimeFromDate(stop.provider_time_from)) {
          errors[
            `stop-time-from-${stop.order}`
          ] = `The start time for Stop #${stop.order} must be in hh:mm format`;
          errorInputName = `stop-time-from-${stop.order}`;
          break;
        }

        if (!!stop.provider_time_to && !DateUtils.validateTimeFromDate(stop.provider_time_to)) {
          errors[
            `stop-time-to-${stop.order}`
          ] = `The end time for Stop #${stop.order} must be in hh:mm format`;
          errorInputName = `stop-time-to-${stop.order}`;
          break;
        }
      }
      if (errorInputName) return [errors, errorInputName];
    }

    return [errors, errorInputName];
  };

  const scrollToInput = (inputName: string): void => {
    const input = document.querySelector(`input[name=${inputName}]`);

    input?.scrollIntoView({
      block: 'center'
    });
  };

  const validate = (values: FormikValues): void | FormikErrors<FormikValues> => {
    const [errors, errorInputName] = getErrorInfo(values);

    if (errorInputName) {
      const errorMessages = Object.values(errors);
      if (errorMessages && errorMessages.length > 0) {
        addNotification({
          message: errorMessages[0] as string,
          variant: 'error'
        });
      }
      scrollToInput(errorInputName);
    }
    return errors;
  };

  return (
    <Formik
      initialValues={{}}
      validate={validate}
      onSubmit={(): void => {
        onSubmit();
      }}
      validateOnChange={false}
      validateOnBlur={false}
      validateOnMount={false}
    >
      <Form ref={formRef}>{children}</Form>
    </Formik>
  );
};

export default TripWrapperForm;
